import type React from 'react'
import { useTranslation } from 'react-i18next'
import { DollarSign, Search, Star } from 'lucide-react'
import type { AuditReport } from '@/lib/api/amazon-listing'

interface KeyMetricsRowProps {
  pricingAnalysis?: AuditReport['pricing_analysis']
  keywordMatrix?: AuditReport['keyword_matrix']
  reviewInsights?: AuditReport['review_insights']
}

export function KeyMetricsRow({ pricingAnalysis, keywordMatrix, reviewInsights }: KeyMetricsRowProps) {
  const { t } = useTranslation()
  const r = (key: string) => t(`amazonListing.potentialCheck.${key}`)

  const metrics = [
    pricingAnalysis && {
      icon: <DollarSign className="w-4 h-4 text-green-500" />,
      label: r('pricingPosition'),
      value: `P${pricingAnalysis.price_position.percentile}`,
      sub: `${pricingAnalysis.price_position.source_price}€`,
    },
    keywordMatrix && {
      icon: <Search className="w-4 h-4 text-primary-500" />,
      label: r('keywordCoverage'),
      value: `${keywordMatrix.coverage?.coverage_percent ?? 0}%`,
      sub: `${keywordMatrix.coverage?.covered_anywhere ?? 0}/${keywordMatrix.coverage?.total_keywords ?? 0}`,
    },
    reviewInsights && {
      icon: <Star className="w-4 h-4 text-yellow-500" />,
      label: r('reviewSentiment'),
      value: `${reviewInsights.overview.average_rating}★`,
      sub: t('amazonListing.potentialCheck.reviewsCount', { count: reviewInsights.overview.total_reviews }),
    },
  ].filter(Boolean) as Array<{ icon: React.ReactNode; label: string; value: string; sub: string }>

  if (metrics.length === 0) return null

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {metrics.map((m) => (
        <div key={m.label} className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 px-4 py-3 flex items-center gap-3">
          {m.icon}
          <div className="min-w-0">
            <div className="text-xs text-neutral-500 dark:text-neutral-400">{m.label}</div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-bold text-neutral-900 dark:text-white">{m.value}</span>
              <span className="text-xs text-neutral-400">{m.sub}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
